import {Model} from 'MWC';

const installment_from_statement = 'installment_from_statement',
    oneTimeCommission = 'one_time_commission',
    timeUnit = 'time_unit',
    realRate = 'real_rate',
    months = 12,
    hundredPercent = 100,
    daysInTheYear = 365,
    bankDays = 360;

export default class extends Model {
    static fields;

    constructor(data, selectedType) {
        super();
        this.selectedType = installment_from_statement;
        this.creditSum = data.min;
        this.loanTerm = 0;
        this.requiredPayment = 0;
        this.overpayment = 0;
        this.creditCost = 0;
        this._initFields(data);
    }

    _initFields(data) {
        this.fields = {
            ...data,
            fix_amounts: ['500', '5000', '10000', '20000'],
            realShownRates: ['0.00001', '0.00001', '109', '109']
        };
    }

    changeField(name, value) {
        this[name] = value;
        this.calculateIndicators();
    }

    getCreditSum() {
        return +this.creditSum;
    }

    setCreditSum(value) {
        this.creditSum = value;
    }

    getMinSumEdge() {
        return +this.fields.min;
    }

    getFixAmounts() {
        return this.fields.fix_amounts;
    }

    getMaxSumEdge() {
        return +this.fields.max;
    }

    getSumStep() {
        return +this.fields.step;
    }

    getTerms() {
        return this.fields.terms;
    }

    getCurrentLoanTerm() {
        return this.loanTerm;
    }

    getMinTermEdge() {
        return this.fields.terms[0];
    }

    getMaxTermEdge() {
        return this.fields.terms[this.fields.terms.length - 1];
    }

    setCurrentLoanTerm(value) {
        this.loanTerm = value;
    }

    getTimeUnit() {
        return this.fields[timeUnit];
    }

    getCreditTerm() {
        return +this.getTerms()[this.getCurrentLoanTerm()];
    }

    getCreditRate() {
        return this.fields.rate;
    }

    getRealRate() {
        const term = this.getCurrentLoanTerm();

        return this.fields[realRate][term];
    }

    getShownRealRate() {
        const term = this.getCurrentLoanTerm();
        return this.fields.realShownRates[term];
    }

    getMonthCommission() {
        return this.fields.month_commission;
    }

    getOneTimeCommission() {
        return this.fields[oneTimeCommission];
    }

    getRequiredPayment() {
        return this.requiredPayment;
    }

    setRequiredPayment(value) {
        this.requiredPayment = value;
    }

    getOverpayment() {
        return this.overpayment;
    }

    setOverpayment(value) {
        this.overpayment = value;
    }

    getCreditCost() {
        return this.creditCost;
    }

    setCreditCost(value) {
        this.creditCost = value;
    }

    getCoefficient() {
        return this.fields.coefficient[this.getCurrentLoanTerm()];
    }

    getMaxDays() {
        const maxDays = this.fields.max_days;

        if (!maxDays) {
            return null;
        }

        return maxDays[this.getCurrentLoanTerm()] || null;
    }

    calculateCreditSum() {
        const creditSum = this.getCreditSum();
        return +creditSum + (creditSum * this.getOneTimeCommission()) / hundredPercent;
    }

    resetIndicators() {
        this.setCreditSum(this.getMinSumEdge());
        this.setCurrentLoanTerm(0);
    }

    _round2(value) {
        return Math.round(value * 100) / 100;
    }

    calculateIndicators() {
        const creditSum = this.calculateCreditSum();
        const term = this.getCreditTerm();

        const coefficient = this.getCoefficient();

        if (coefficient != null && coefficient > 0) {
            const rawRequiredPayment = creditSum * coefficient;

            const requiredPayment = this._round2(rawRequiredPayment);

            const rawTotal = rawRequiredPayment * term;

            const overpayment = this._round2(rawTotal - creditSum);

            const creditCost = this._round2(rawTotal);

            this.setRequiredPayment(requiredPayment);
            this.setOverpayment(overpayment);
            this.setCreditCost(creditCost);

            return;
        }

        const interestRate = this.getRealRate(),
            monthInterestRate = interestRate / months,
            monthCommission = this.getMonthCommission();

        const requiredPayment = this._round2(
                (((creditSum * interestRate) / months / hundredPercent) * daysInTheYear) /
                    bankDays /
                    (1 -
                        Math.pow(
                            1 + ((monthInterestRate / hundredPercent) * daysInTheYear) / bankDays,
                            -term
                        )) +
                    (creditSum * monthCommission) / hundredPercent
            ),
            overpayment =
                term === 3 || term === 6 ? 0.01 : this._round2(requiredPayment * term - creditSum);

        this.setRequiredPayment(requiredPayment);
        this.setOverpayment(overpayment);
        const creditCost = creditSum + overpayment;
        this.setCreditCost(creditCost);
    }
}
